import { AppConfigurationService } from 'src/app/shared/services/app-configuration.service';
import { Component, OnDestroy, AfterViewChecked, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ChatState } from '../../shared/enums/chat-state.enum';
import { ChatApiService } from '../../shared/services/api/chat-api.service';
import { ChatMessageHistoryService } from '../../shared/services/chat-message-history.service';
import { Chat, ChatMessage, ChatNotification, ChatContext, BotContext, ChatMemory, Todo, ChatUpdates } from '../../shared/types/chat.types';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  host: {
    '[class.chat-expanded]': 'isExpanded',
  },
  standalone: false,
})
export class ChatComponent implements OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer', { static: false }) messagesContainer!: ElementRef;

  // Component state
  chatId = '';
  messages: ChatMessage[] = [];
  currentMessage = '';
  sendingMessage = false;
  currentQuestion: string | null = null; // Track question being processed (from any tab)
  botContext: BotContext = { id: '' };
  stage = '';
  info = '';
  todos: Todo[] = [];
  contextProcessLabel = '';
  contextQueueLabel = '';
  examples: string[] = [];
  isExpanded = false;
  state = ChatState.Ready;
  memory: ChatMemory = { debugging_enabled: false };

  // Make ChatState enum available to template
  readonly ChatState = ChatState;

  // Panel resize properties
  panelWidth = 450;
  private readonly minPanelWidth = 450;
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;

  // Private properties
  private currentVersion = 0;
  private pollingActive = false;
  private stopPolling$ = new Subject<void>();
  private shouldScrollToBottom = false;

  get showLoadingTodos(): boolean {
    return this.todos.length > 0;
  }

  constructor(
    private chatApiService: ChatApiService,
    private messageHistoryService: ChatMessageHistoryService,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigurationService: AppConfigurationService
  ) { }

  ngOnDestroy(): void {
    this.stopPolling();
    // Restore layout when component is destroyed
    this.isExpanded = false;
    this.adjustLayoutForChatPanel();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  get toolbarDisabled(): boolean {
    return this.sendingMessage;
  }

  get showContextInfo(): boolean {
    return !!(this.contextProcessLabel || this.contextQueueLabel);
  }

  get showLoadingInfo(): boolean {
    return !!this.info;
  }

  async initialize(): Promise<void> {
    this.state = ChatState.Loading;
    let result: Chat;

    const [chats, apiOk] = await this.chatApiService.getChatList();
    if (!apiOk) {
      console.error('Failed to load chat list');
      this.state = ChatState.Error;
      return;
    }
    const existingChatId = chats.length > 0 ? chats[0].id : null;
    if (existingChatId) {
      try {
        result = await this.chatApiService.getChat(existingChatId);
      } catch (error) {
        console.warn('Failed to load existing chat, creating new one:', error);
        result = await this.chatApiService.createChat();
      }
    } else {
      result = await this.chatApiService.createChat();
    }

    this.chatId = result.id;
    this.messages = result.messages;
    this.currentMessage = '';
    this.botContext = { id: result.id };
    this.memory = result.memory ?? { debugging_enabled: false };
    this.currentVersion = result.latest_version ?? 0;
    this.currentQuestion = result.current_question ?? null;

    this.startPolling();
    this.shouldScrollToBottom = true;
    this.state = ChatState.Ready;
  }

  async newChat(): Promise<void> {
    if (this.sendingMessage) return;

    this.stopPolling();

    const result = await this.chatApiService.createChat();
    this.chatId = result.id;
    this.messages = result.messages;
    this.currentMessage = '';
    this.botContext = { id: result.id };
    this.stage = '';
    this.info = '';
    this.todos = [];
    this.contextProcessLabel = '';
    this.contextQueueLabel = '';
    this.currentVersion = result.latest_version ?? 0;
    this.currentQuestion = result.current_question ?? null;

    this.startPolling();
    this.shouldScrollToBottom = true;
  }

  async sendMessage(messageOverride?: string): Promise<void> {
    if (this.sendingMessage || this.currentQuestion) return;

    const messageToSend = messageOverride ?? this.currentMessage;
    if (!messageToSend.trim()) return;

    this.messageHistoryService.addMessage(messageToSend);

    // Add temporary user message to show immediately in the UI
    const tempUserMessage: ChatMessage = {
      role: 'human',
      text: messageToSend,
      version: -1 // Temporary marker
    };
    this.messages = [...this.messages, tempUserMessage];
    this.currentMessage = '';
    this.shouldScrollToBottom = true;
    this.sendingMessage = true;

    try {
      await this.chatApiService.sendMessage(this.chatId, messageToSend);
      // Messages (including user message) are delivered via the polling endpoint only
      // Clear notifications/state while waiting for response
      this.stage = '';
      this.info = '';
      this.todos = [];
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove temporary message on error
      this.messages = this.messages.filter(m => m.version !== -1);
      this.sendingMessage = false;
      this.currentMessage = '';
      this.stage = '';
      this.info = '';
      this.todos = [];
    }
  }

  async sendExampleMessage(message: string): Promise<void> {
    this.currentMessage = message;
    await this.sendMessage();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      void this.sendMessage();
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowUp') {
      event.preventDefault();
      this.navigateHistory('up');
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowDown') {
      event.preventDefault();
      this.navigateHistory('down');
    }
  }

  navigateHistory(direction: 'up' | 'down'): void {
    if (direction === 'up') {
      this.currentMessage = this.messageHistoryService.getPreviousMessage();
    } else if (direction === 'down') {
      this.currentMessage = this.messageHistoryService.getNextMessage();
    }
  }

  async reloadChat(): Promise<void> {
    if (!this.chatId) {
      return;
    }

    const result = await this.chatApiService.getChat(this.chatId);
    this.messages = result.messages;
    this.currentMessage = '';
    this.botContext = { id: result.id };
    this.memory = result.memory ?? { debugging_enabled: false };
    this.currentVersion = result.latest_version ?? 0;
    this.currentQuestion = result.current_question ?? null;

    this.startPolling();
    this.shouldScrollToBottom = true;
    this.state = ChatState.Ready;
  }

  async toggleChat(toggle: boolean | null = null): Promise<void> {
    this.isExpanded = toggle != null ? toggle : !this.isExpanded;
    this.adjustLayoutForChatPanel();
    if (this.isExpanded) {
      await this.initialize();
      this.changeDetectorRef.markForCheck();
      this.scrollToBottom();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any): void {
    if (this.isExpanded) {
      this.adjustLayoutForChatPanel();
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isResizing) {
      const deltaX = this.startX - event.clientX;
      const newWidth = Math.max(this.minPanelWidth, this.startWidth + deltaX);
      this.panelWidth = newWidth;
      this.adjustLayoutForChatPanel();
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isResizing) {
      this.isResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }

  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  private adjustLayoutForChatPanel(): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const mainElement = document.querySelector('.main-body') as HTMLElement;
    if (mainElement) {
      const marginAdjustment = window.innerWidth <= 768 ? '0' : `${this.panelWidth}px`;

      if (this.isExpanded) {
        mainElement.style.marginRight = marginAdjustment;
        mainElement.style.transition = 'margin-right 0.3s ease-out';
      } else {
        mainElement.style.marginRight = '0';
        mainElement.style.transition = 'margin-right 0.3s ease-out';
      }
    }
  }

  private startPolling(): void {
    if (this.pollingActive) {
      return;
    }

    this.pollingActive = true;
    this.pollForUpdates();
  }

  private pollForUpdates(): void {
    if (!this.pollingActive) {
      return;
    }

    this.chatApiService.getUpdates$(this.chatId, this.currentVersion)
      .pipe(takeUntil(this.stopPolling$))
      .subscribe({
        next: (updates) => {
          if (!this.pollingActive) {
            return;
          }
          this.processUpdates(updates);
          // Continue polling
          this.pollForUpdates();
        },
        error: (error) => {
          if (!this.pollingActive) {
            console.log('Update polling stopped');
            return;
          }
          console.error('Polling error:', error);
          // Retry after 5 seconds
          setTimeout(() => this.pollForUpdates(), 5000);
        }
      });
  }

  private processUpdates(updates: ChatUpdates): void {
    // Update current_question state to track when processing starts/completes (from any tab)
    if (updates.current_question !== undefined) {
      this.currentQuestion = updates.current_question;

      // If another tab sent a message, add it as temporary until real message arrives
      if (updates.current_question !== null && !this.sendingMessage) {
        // Another tab sent this question
        const hasTemp = this.messages.some(m => m.version === -1);
        if (!hasTemp) {
          const tempUserMessage: ChatMessage = {
            role: 'human',
            text: updates.current_question,
            version: -1
          };
          this.messages = [...this.messages, tempUserMessage];
          this.shouldScrollToBottom = true;
        }
      }

      if (updates.current_question === null) {
        // Processing completed - clear flags and textbox only if we were sending
        const wasSending = this.sendingMessage;
        this.sendingMessage = false;
        if (wasSending) {
          // Only clear the textbox if we were the ones sending the message
          this.currentMessage = '';
        }
      }
    }

    // Process new messages
    if (updates.messages.length > 0) {
      // Remove temporary messages before adding real ones
      const messagesWithoutTemp = this.messages.filter(m => m.version !== -1);
      this.messages = [...messagesWithoutTemp, ...updates.messages];
      this.shouldScrollToBottom = true;

      // Clear notifications when AI response messages arrive
      const hasAiMessage = updates.messages.some(m => m.role === 'ai');
      if (hasAiMessage) {
        this.todos = [];
        this.info = '';
      }
    }

    // Process notifications
    updates.notifications.forEach(notification => {
      this.handleNotification(notification);
    });

    // Update version
    if (updates.latest_version > this.currentVersion) {
      this.currentVersion = updates.latest_version;
    }

    this.changeDetectorRef.markForCheck();
  }

  private handleNotification(notification: ChatNotification): void {
    switch (notification.type) {
      case 'command':
        if (notification.message === 'debugging_on') {
          this.memory.debugging_enabled = true;
        }
        if (notification.message === 'debugging_off') {
          this.memory.debugging_enabled = false;
        }
        break;
      case 'stage':
        this.stage = notification.message;
        this.shouldScrollToBottom = true;
        break;
      case 'info':
      case 'error':
        // debug is too verbose to show on UI
        // case 'debug':
        this.info = notification.message;
        this.shouldScrollToBottom = true;
        break;
      case 'todos':
        try {
          const todos = JSON.parse(notification.message) as Todo[];
          this.todos = todos;
          this.shouldScrollToBottom = true;
        } catch (e) {
          console.error('Failed to parse todos notification:', e);
        }
        break;
      case 'context':
        try {
          const context = JSON.parse(notification.message) as ChatContext;
          this.contextProcessLabel = context.process_label || '';
          this.contextQueueLabel = context.queue_label || '';
          this.shouldScrollToBottom = true;
        } catch (e) {
          console.error('Failed to parse context notification:', e);
        }
        break;
    }
    if (this.memory.debugging_enabled) {
      console.log(`${notification.type}: ${notification.message}`);
    }
    this.changeDetectorRef.markForCheck();
  }

  private stopPolling(): void {
    this.pollingActive = false;
    this.stopPolling$.next();
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      setTimeout(() => {
        const element = this.messagesContainer.nativeElement as HTMLElement;
        element.scrollTop = element.scrollHeight;
      }, 100);
    }
  }
}
